import React from "react";
import {
  Box,
  Typography,
  Button,
} from "@mui/material";
import { PlayArrow } from "@mui/icons-material";
import { ExtractedContract } from "../../../types/contract";

interface ActionButtonGroupProps {
  selectedContract: ExtractedContract | null;
  selectedEditableContract: ExtractedContract;
  isEditing: boolean;
  isTrashView: boolean;
  hasUnsavedChanges: boolean;
  aggregatedContracts: ExtractedContract[];
  changedContractIds: Set<number>;
  handlePrev: () => void;
  handleNext: () => void;
  handleRestore: (contract: ExtractedContract) => void;
  handleSaveSingleContractChanges: () => void;
  handleCancelEdit: () => void;
  handleStartEdit: () => void;
  handleDeleteDetails: (contract: ExtractedContract) => void;
  confirmAndNavigate: (action: () => void) => void;
  navigate: (path: string) => void;
}

const ActionButtonGroup: React.FC<ActionButtonGroupProps> = ({
  selectedContract,
  selectedEditableContract,
  isEditing,
  isTrashView,
  hasUnsavedChanges,
  aggregatedContracts,
  changedContractIds,
  handlePrev,
  handleNext,
  handleRestore,
  handleSaveSingleContractChanges,
  handleCancelEdit,
  handleStartEdit,
  handleDeleteDetails,
  confirmAndNavigate,
  navigate,
}) => {
  return (
    <>
      <Box
        sx={{
          mt: 2,
          pt: 2,
          borderTop: "1px solid #e0e0e0",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, width: '100%' }}>
          <Button
            onClick={handlePrev}
            disabled={!selectedContract || aggregatedContracts.findIndex(c => c.id === selectedContract.id) === 0 || hasUnsavedChanges}
            sx={{
              border: 'none',
              background: 'none',
              padding: 0,
              minWidth: '50px',
              minHeight: '50px',
              fontSize: '3rem',
              color: 'rgb(64, 144, 255)',
              '&:hover': { background: 'none', color: 'rgba(50, 119, 215, 1)' }
            }}
          >
            ◀
          </Button>
          <Box sx={{ display: "flex", gap: 3 }}>
            {isTrashView ? (
              <Button
                variant="contained"
                color="success"
                sx={{
                  fontWeight: "bold",
                  fontSize: "16px",
                  pl: 4,
                  pr: 4,
                  backgroundColor: "rgb(50, 225, 170)",
                  border: "4px solid rgba(240, 232, 249, 0.9)",
                  borderRadius: "25px",
                  boxShadow: "4px 4px 8px rgba(0, 0, 0, 0.3)",
                  "&:hover": {
                    backgroundColor: "rgb(50, 225, 170)",
                    border: "4px solid rgba(240, 232, 249, 0.9)",
                    borderRadius: "25px",
                  },
                }}
                onClick={() => handleRestore(selectedEditableContract)}
              >
                このデータを復元
              </Button>
            ) : isEditing ? (
              <Box sx={{ display: "flex", gap: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!selectedEditableContract || !selectedEditableContract.id || !changedContractIds.has(selectedEditableContract.id)}
                  sx={{
                    fontWeight: "bold",
                    fontSize: "16px",
                    pl: 4,
                    pr: 4,
                    backgroundColor: "rgb(64, 144, 255)",
                    border: "4px solid rgba(240, 232, 249, 0.9)",
                    borderRadius: "25px",
                    boxShadow: "4px 4px 8px rgba(0, 0, 0, 0.3)",
                    "&:hover": {
                      backgroundColor: "rgb(64,144, 255)",
                      border: "4px solid rgba(240, 232, 249, 0.9)",
                      borderRadius: "25px",
                    },
                  }}
                  onClick={() => handleSaveSingleContractChanges()}
                >
                  保存
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  sx={{
                    pr: 4,
                    pl: 4,
                    pt: 1,
                    pb: 1,
                    color: "rgba(255, 255, 255, 1)", // Blue text
                    backgroundColor: "rgba(179, 175, 175, 1)",
                    border: "4px solid rgba(240, 232, 249, 0.9)", // Blue border
                    borderRadius: "25px",
                    boxShadow: "4px 4px 8px rgba(0, 0, 0, 0.3)",
                    fontWeight: "bold",
                    fontSize: "1rem",
                    "&:hover": {
                      backgroundColor: "rgba(206, 206, 206, 1)", // Darker gray for subtle hover
                      border: "4px solid rgba(240, 232, 249, 0.9)",
                      borderRadius: "25px",
                    },
                  }}
                  onClick={() => handleCancelEdit()}
                >
                  キャンセル
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: "flex", gap: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{
                    fontWeight: "bold",
                    fontSize: "16px",
                    pl: 4,
                    pr: 4,
                    backgroundColor: "rgba(255, 189, 23, 1)",
                    border: "4px solid rgba(240, 232, 249, 0.9)",
                    borderRadius: "25px",
                    boxShadow: "4px 4px 8px rgba(0, 0, 0, 0.3)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 189, 23, 1)",
                      border: "4px solid rgba(240, 232, 249, 0.9)",
                      borderRadius: "25px",
                    },
                  }}
                  onClick={() => handleStartEdit()}
                >
                  編集
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  sx={{
                    fontWeight: "bold",
                    fontSize: "16px",
                    pl: 4,
                    pr: 4,
                    backgroundColor: "rgba(255, 95, 149, 1)",
                    border: "4px solid rgba(240, 232, 249, 0.9)",
                    borderRadius: "25px",
                    boxShadow: "4px 4px 8px rgba(0, 0, 0, 0.3)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 95, 149, 1)",
                      border: "4px solid rgba(240, 232, 249, 0.9)",
                      borderRadius: "25px",
                    },
                  }}
                  onClick={() =>
                    handleDeleteDetails(selectedEditableContract)
                  }
                  disabled={isEditing}
                >
                  削除
                </Button>
              </Box>
            )}
          </Box>
          <Button
            onClick={handleNext}
            disabled={!selectedContract || aggregatedContracts.findIndex(c => c.id === selectedContract.id) === aggregatedContracts.length - 1 || hasUnsavedChanges}
            sx={{
              border: 'none',
              background: 'none',
              padding: 0,
              minWidth: '50px',
              minHeight: '50px',
              fontSize: '3rem',
              color: 'rgb(64, 144, 255)',
              '&:hover': { background: 'none', color: 'rgba(50, 119, 215, 1)' }
            }}
          >
            ▶
          </Button>
        </Box>
      </Box>
      <Box
        sx={{
          mt: 1,
          mb: 1,
          pt: 2,
          ml: 2,
          mr: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Button
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between", // Pushes the 3 items apart
              textAlign: "center",
              pr: 4, // Increased horizontal padding
              pl: 4, // Increased horizontal padding
              pt: 0, // Added vertical padding
              pb: 0, // Added vertical padding
              color: "white",
              backgroundColor: "rgb(64, 144, 255)",
              border: "4px solid rgba(240, 232, 249, 0.9)",
              borderRadius: "50px",
              boxShadow: "-6px -6px 5px rgb(255, 255, 255), 4px 4px 20px rgba(0, 0, 0, 0.2)",
              "&:hover": {
                backgroundColor: "rgb(64,144, 255)",
                border: "4px solid rgba(240, 232, 249, 0.9)",
                borderRadius: "50px",
              },
            }}
            variant="contained"
            onClick={() => confirmAndNavigate(() => navigate("/"))}
          >
            {/* Left Item */}
            <PlayArrow sx={{ fontSize: "16px", transform: 'rotate(180deg)' }} />

            {/* Center Item */}
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Typography
                variant="caption"
                component="span"
                sx={{ fontWeight: "bold" }}
              >
                アップロード画面に
              </Typography>
              <Typography
                variant="body1"
                component="span"
                sx={{ fontWeight: "bold" }}
              >
                戻る
              </Typography>
            </Box>

            {/* Right Placeholder to balance the icon */}
            <Box sx={{ width: '16px', visibility: 'hidden' }} />
          </Button>
        </Box>

      </Box>
    </>
  );
};

export default ActionButtonGroup;
